import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Row, Select } from 'antd';
import {
  FieldTimeOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { isNullOrUndefined } from 'ontraccr-common/lib/Common';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import WorkflowHandle from './WorkflowHandle';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowTimeConfigureDrawer from './WorkflowTimeConfigureDrawer';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';

import { getType, updateData } from './workflowHelpers';
import { safeExtractFromObject } from '../../helpers/helpers';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';
import CostcodesTreeSelector from '../../costcodes/CostcodesTreeSelector';
import { TIME_TYPE_FLAG } from '../../constants/Flags';
import { resolveFormDivisionIds } from '../formHelpers';

const HOVER_TEXT = (
  <div>
    Allows the creation or update of Time Card entries though a Time Entry Table, Time Tracking Grid or individual fields.
  </div>
);

const DISABLED_TEXT = 'Add a Time Entry Table, Time Tracking Grid (not preloaded from existing time entries), Date/Time, or Date/Time Range field to enable this step';

const getErrorDetails = ({
  stateUseTable,
  timeTableId,
  dateFieldId,
  userFieldId,
  reqProject,
  reqCostcode,
  validationState,
  isExternalForm,
  stateUseTimeTrackingGrid,
  timeTrackingGridId,
}) => {
  if (stateUseTable || stateUseTimeTrackingGrid) {
    if (!timeTableId && !timeTrackingGridId) {
      return {
        content: (
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#ff4d4f' }}>
              ⚠️ Configuration Required
            </div>
            <div style={{ lineHeight: 1.5 }}>
              This step requires a <b>Time Entry Table</b> or <b>Time Tracking Grid</b> to be selected.
            </div>
          </div>
        ),
      };
    }
    return null;
  }

  const errors = [];

  if (!dateFieldId) errors.push('Date');
  if (!validationState.hasHours) errors.push('Hours');
  if (!userFieldId) errors.push('User');
  if (userFieldId === 'formAuthor' && isExternalForm) errors.push('Form Author cannot be used in external forms');
  if (reqProject && !validationState.hasProject) errors.push('Project');
  if (reqCostcode && !validationState.hasCostcode) errors.push('Costcode');
  if (errors.length === 0) return null;

  const errorText = errors.length === 1
    ? errors[0]
    : `${errors.slice(0, -1).join(', ')}, and ${errors[errors.length - 1]}`;

  return {
    content: (
      <div style={{ maxWidth: 300 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#ff4d4f' }}>
          ⚠️ Missing Required Fields
        </div>
        <div style={{ lineHeight: 1.5 }}>
          Please configure the following required field{errors.length > 1 ? 's' : ''}: <b>{errorText}</b>
        </div>
      </div>
    ),
  };
};

export default function WorkflowCreateTimeNode({
  setElements,
  setDataMap,
  isDisplay,
  divisionId,
  divisionIds = [],
  sections = [],
  name,
  setDisableNext,
  isExternalForm,
} = {}) {
  const relevantDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

  // eslint-disable-next-line react/function-component-definition
  const WorkflowCreateTimeNodeComponent = function WorkflowCreateTimeNodeComponent({
    id,
    draggable,
    disabled,
    data = {},
  }) {
    const {
      fieldMappings: initialFieldMappings = {},
      tableFieldId,
      timeTrackingGridFieldId,
      autoSubmit: initialAutoSubmit,
      autoApprove: initialAutoApprove,
      preventDuplicates: initialPreventDuplicates,
      dateFieldId: initialDateFieldId,
      userFieldId: initialUserFieldId,
      hoursFieldId: initialHoursFieldId,
      projectFieldId: initialProjectFieldId,
      costcodeFieldId: initialCostcodeFieldId,
      staticHours: initialStaticHours,
      staticProject: initialStaticProject,
      staticCostcode: initialStaticCostcode,
      allowFutureDates: initialAllowFutureDates,
    } = data;

    const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
    const paidFlags = useSelector((state) => safeExtractFromObject(state, 'settings.company.paidFlags'));
    const customFields = useSelector((state) => state.timecards.customFields);
    const projects = useSelector((state) => state.projects.projects);

    const {
      reqProject,
      reqCostcode,
      enableTimecardSubmission,
    } = settings;

    const hasMultiTimeEntryTypes = paidFlags?.some((flag) => flag === TIME_TYPE_FLAG);

    const title = `Create/Update Time Entries${disabled ? ' - DISABLED' : ''}`;

    const [showDrawer, setShowDrawer] = useState(false);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [timeTableId, setTimeTableId] = useState(tableFieldId);
    const [timeTrackingGridId, setTimeTrackingGridId] = useState(timeTrackingGridFieldId);
    const [autoSubmit, setAutoSubmit] = useState(initialAutoSubmit);
    const [autoApprove, setAutoApprove] = useState(initialAutoApprove);
    const [preventDuplicates, setPreventDuplicates] = useState(initialPreventDuplicates);
    const [stateUseTable, setStateUseTable] = useState(!!tableFieldId);
    const [stateUseTimeTrackingGrid, setStateUseTimeTrackingGrid] = useState(!!timeTrackingGridFieldId);
    const [dateFieldId, setDateFieldId] = useState(initialDateFieldId);
    const [userFieldId, setUserFieldId] = useState(initialUserFieldId);
    const [hoursFieldId, setHoursFieldId] = useState(initialHoursFieldId);
    const [projectFieldId, setProjectFieldId] = useState(initialProjectFieldId);
    const [costcodeFieldId, setCostcodeFieldId] = useState(initialCostcodeFieldId);
    const [staticHours, setStaticHours] = useState(initialStaticHours);
    const [staticProject, setStaticProject] = useState(initialStaticProject);
    const [staticCostcode, setStaticCostcode] = useState(initialStaticCostcode);
    const [allowFutureDates, setAllowFutureDates] = useState(initialAllowFutureDates ?? false);

    const openDrawer = useCallback(() => setShowDrawer(true), []);
    const closeDrawer = useCallback(() => setShowDrawer(false), []);

    const setMap = {
      userFieldId: setUserFieldId,
      projectFieldId: setProjectFieldId,
      costcodeFieldId: setCostcodeFieldId,
      hoursFieldId: setHoursFieldId,
      staticHours: setStaticHours,
      staticProject: setStaticProject,
      staticCostcode: setStaticCostcode,
    };

    const validationState = useMemo(() => {
      const hasHours = (hoursFieldId && hoursFieldId !== 'staticHours') || (hoursFieldId === 'staticHours' && !isNullOrUndefined(staticHours));
      const hasProject = (projectFieldId && projectFieldId !== 'staticProject') || (projectFieldId === 'staticProject' && !isNullOrUndefined(staticProject));
      const hasCostcode = (costcodeFieldId && costcodeFieldId !== 'staticCostcode') || (costcodeFieldId === 'staticCostcode' && !isNullOrUndefined(staticCostcode));

      return {
        hasHours,
        hasProject,
        hasCostcode,
      };
    }, [
      hoursFieldId,
      staticHours,
      projectFieldId,
      staticProject,
      costcodeFieldId,
      staticCostcode,
    ]);

    const disableNext = useMemo(() => {
      if (stateUseTable) return !timeTableId;
      if (stateUseTimeTrackingGrid) return !timeTrackingGridId;

      return (
        !dateFieldId
        || !validationState.hasHours
        || !userFieldId || (userFieldId === 'formAuthor' && isExternalForm)
        || (reqProject && !validationState.hasProject)
        || (reqCostcode && !validationState.hasCostcode)
      );
    }, [
      stateUseTable,
      timeTableId,
      timeTrackingGridId,
      stateUseTimeTrackingGrid,
      dateFieldId,
      userFieldId,
      reqProject,
      reqCostcode,
      validationState,
      isExternalForm,
    ]);

    useEffect(() => {
      if (!setDisableNext) return;

      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [disableNext, setDisableNext, id]);

    const filteredProjects = useMemo(() => (
      projects.filter((project) => project.divisionId === divisionId && project.active)
        .map((project) => ({
          label: formatProjectLabelFromCompanySettings({
            name: project.name,
            number: project.number,
            settings,
          }),
          value: project.id,
        }))
    ), [projects, divisionId, settings]);

    const hasConfigurableCustomFields = useMemo(() => (
      relevantDivisionIds.some((divId) => {
        const fields = customFields[divId]?.fields ?? [];
        return fields.some((section) => section.id !== 'timecard');
      })
    ), [customFields, relevantDivisionIds]);

    const {
      timeTables,
      timeTrackingGrids,
      dateFields,
      userFields,
      numericalFields,
      projectFields,
      costcodeFields,
    } = useMemo(() => {
      const time = [];
      const date = [];
      const numerical = [];
      const user = [];
      const project = [];
      const costcode = [];
      const grids = [];
      sections.forEach(({ fields = [] }) => (
        fields.forEach((field) => {
          const type = getType(field);
          const parsedField = {
            label: field.configProps.title,
            value: field.id,
          };
          if (type === 'table - TimeEntry') {
            time.push(parsedField);
          } else if (type === 'dateTime' || type === 'dateRange') {
            date.push(parsedField);
          } else if (type === 'dropdown - Users') {
            user.push(parsedField);
          } else if (type === 'dropdown - Projects') {
            project.push(parsedField);
          } else if (type === 'dropdown - Costcodes') {
            costcode.push(parsedField);
          } else if (type === 'text' && field.configProps.isNumerical) {
            numerical.push(parsedField);
          } else if (type === 'timeTrackingGrid') {
            if (field.configProps.timeTrackingGridPreloadSource !== 'timeEntries') {
              grids.push(parsedField);
            }
          }
        })
      ));
      return {
        timeTables: time,
        dateFields: date,
        numericalFields: [{ label: 'Static Hours', value: 'staticHours' }].concat(numerical),
        userFields: [{ label: 'Form Data - Form Author', value: 'formAuthor' }].concat(user),
        projectFields: [{ label: 'Static Project', value: 'staticProject' }].concat(project),
        costcodeFields: costcode,
        timeTrackingGrids: grids,
      };
    }, [sections]);

    useEffect(() => {
      if (!timeTrackingGrids?.length && !!timeTrackingGridId) {
        setTimeTrackingGridId(null);
        setDataMap(updateData(id, { timeTrackingGridFieldId: null }));
      }
    }, [timeTrackingGrids, timeTrackingGridId, setDataMap, id]);

    const finalCostcodeFields = useMemo(() => {
      if (projectFieldId !== 'staticProject') return costcodeFields;

      return [{ label: 'Static Costcode', value: 'staticCostcode' }].concat(costcodeFields);
    }, [costcodeFields, projectFieldId]);

    const onCheckChanged = useCallback((key) => (newChecked) => {
      if (!setDataMap || !id) return;
      const update = { [key]: newChecked };
      if (key === 'autoSubmit') {
        setAutoSubmit(newChecked);
        if (!newChecked) {
          update.autoApprove = false;
          setAutoApprove(false);
        }
      } else if (key === 'autoApprove') {
        setAutoApprove(newChecked);
      } else if (key === 'preventDuplicates') {
        setPreventDuplicates(newChecked);
      } else if (key === 'allowFutureDates') {
        setAllowFutureDates(newChecked);
      } else if (key === 'useTable' || key === 'useTimeTrackingGrid') {
        delete update.useTable;
        delete update.useTimeTrackingGrid;
        if (key === 'useTable') {
          setStateUseTable(newChecked);
          setStateUseTimeTrackingGrid(false);
        } else {
          setStateUseTimeTrackingGrid(newChecked);
          setStateUseTable(false);
        }
        setTimeTableId(null);
        setTimeTrackingGridId(null);
        setDateFieldId(null);
        update.tableFieldId = null;
        update.dateFieldId = null;
        if (newChecked) { // clear native fields
          setUserFieldId(null);
          setHoursFieldId(null);
          setProjectFieldId(null);
          setCostcodeFieldId(null);
          setStaticHours(null);
          setStaticProject(null);
          setStaticCostcode(null);
          update.userFieldId = null;
          update.hoursFieldId = null;
          update.projectFieldId = null;
          update.costcodeFieldId = null;
          update.staticHours = null;
          update.staticProject = null;
          update.staticCostcode = null;
          update.timeTrackingGridFieldId = null;
          update.tableFieldId = null;
        } else {
          setFieldMappings({});
          update.fieldMappings = {};
        }
      }
      setDataMap(updateData(id, update));
    }, [setDataMap, id]);

    const onFieldMappingsChange = useCallback((newMappings, mappingDivisionId) => {
      if (!setDataMap || !id) return;

      if (relevantDivisionIds.length > 1) {
        const updatedFieldMappings = {
          ...fieldMappings,
          version: 'v2',
          [mappingDivisionId]: newMappings,
        };
        setFieldMappings(updatedFieldMappings);
        setDataMap(updateData(id, { fieldMappings: updatedFieldMappings }));
        return;
      }

      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    }, [setDataMap, id, fieldMappings, relevantDivisionIds]);

    const onTimeTableSelect = useCallback((newFieldId) => {
      if (!setDataMap || !id) return;
      setTimeTableId(newFieldId);
      setDateFieldId(null);
      setDataMap(updateData(id, { tableFieldId: newFieldId, dateFieldId: null }));
    }, [setDataMap, id]);

    const onTimeTrackingGridSelect = useCallback((newFieldId) => {
      if (!setDataMap || !id) return;
      setTimeTrackingGridId(newFieldId);
      setDateFieldId(null);
      setDataMap(updateData(id, { timeTrackingGridFieldId: newFieldId, dateFieldId: null }));
    }, [setDataMap, id]);

    const onDateFieldSelect = useCallback((newFieldId) => {
      if (!setDataMap || !id) return;
      setDateFieldId(newFieldId);
      setTimeTableId(null);
      setDataMap(updateData(id, { dateFieldId: newFieldId, tableFieldId: null }));
    }, [setDataMap, id]);

    const onNativeFieldChange = useCallback((key) => (newFieldId) => {
      if (!setDataMap || !id) return;
      setMap[key]?.(newFieldId);
      const update = { [key]: newFieldId };
      if (key === 'hoursFieldId' && newFieldId !== 'staticHours') {
        update.staticHours = null;
        setStaticHours(null);
      } else if (key === 'projectFieldId' && newFieldId !== 'staticProject') {
        update.staticProject = null;
        update.staticCostcode = null;
        update.costcodeFieldId = null;
        setCostcodeFieldId(null);
        setStaticProject(null);
        setStaticCostcode(null);
      } else if (
        (key === 'costcodeFieldId' && newFieldId !== 'staticCostcode')
        || key === 'staticProject'
      ) {
        update.staticCostcode = null;
        setStaticCostcode(null);
      }
      setDataMap(updateData(id, update));
    }, [setDataMap, id]);

    return (
      <WorkflowActionNode
        title={title}
        Icon={FieldTimeOutlined}
        type="createTime"
        id={id}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : { }}
        errorPopover={disableNext ? getErrorDetails({
          stateUseTable,
          timeTableId,
          dateFieldId,
          userFieldId,
          reqProject,
          reqCostcode,
          validationState,
          isExternalForm,
          stateUseTimeTrackingGrid,
          timeTrackingGridId,
        }) : null}
      >
        {!draggable && (
          <div>
            <OnTraccrCheckbox
              style={{ marginTop: 10 }}
              onChange={onCheckChanged('useTable')}
              defaultChecked={!!tableFieldId}
              value={stateUseTable}
              label="Use Time Entry Table?"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Check this box if you want to link this
                  Create Time Entries step to a Time Entry Table.<br /> <br />
                  Leave this box unchecked if you want to link this step to individual fields.
                </div>
              )}
            />
            {stateUseTable && (
              <>
                <Row style={{ marginTop: 10 }}>
                  Select Table:
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    options={timeTables}
                    optionFilterProp="label"
                    value={timeTableId}
                    onChange={onTimeTableSelect}
                  />
                </Row>
              </>
            )}
            {hasMultiTimeEntryTypes && timeTrackingGrids.length > 0 && (
                <OnTraccrCheckbox
                style={{ marginTop: 10 }}
                onChange={onCheckChanged('useTimeTrackingGrid')}
                defaultChecked={!!timeTrackingGridId}
                value={stateUseTimeTrackingGrid}
                label="Use Time Tracking Grid?"
                hoverNode={(
                  <div style={{ width: 300 }}>
                    Check this box if you want to link this
                    Create Time Entries step to a Time Tracking Grid.<br /> <br />
                    Leave this box unchecked if you want to link this step to individual fields.
                  </div>
                )}
              />
            )}
            {stateUseTimeTrackingGrid && (
              <>
                <Row style={{ marginTop: 10 }}>
                  Select Time Tracking Grid:
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    options={timeTrackingGrids}
                    optionFilterProp="label"
                    value={timeTrackingGridId}
                    onChange={onTimeTrackingGridSelect}
                    allowClear
                  />
                </Row>
              </>
            )}
            {!stateUseTable && !stateUseTimeTrackingGrid && (
              <>
                <Row style={{ marginTop: 10 }}>
                  Date
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    options={dateFields}
                    optionFilterProp="label"
                    value={dateFieldId}
                    onChange={onDateFieldSelect}
                    allowClear
                  />
                </Row>
                <Row style={{ marginTop: 10 }}>
                  Hours
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    options={numericalFields}
                    optionFilterProp="label"
                    value={hoursFieldId}
                    onChange={onNativeFieldChange('hoursFieldId')}
                    allowClear
                  />
                </Row>
                {hoursFieldId === 'staticHours' && (
                  <Row style={{ margin: '10px 0px' }}>
                    <OnTraccrNumberInput
                      value={staticHours}
                      onChange={onNativeFieldChange('staticHours')}
                      autoSize
                      min={0}
                      max={24}
                    />
                  </Row>
                )}
                <Row style={{ marginTop: 10 }}>
                  User
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    options={userFields}
                    optionFilterProp="label"
                    value={userFieldId}
                    onChange={onNativeFieldChange('userFieldId')}
                    allowClear
                  />
                </Row>
                <Row style={{ marginTop: 10 }}>
                  Project
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    options={projectFields}
                    optionFilterProp="label"
                    value={projectFieldId}
                    onChange={onNativeFieldChange('projectFieldId')}
                    allowClear
                  />
                </Row>
                {projectFieldId === 'staticProject' && (
                  <Row style={{ margin: '10px 0px' }}>
                    <Select
                      options={filteredProjects}
                      optionFilterProp="label"
                      value={staticProject}
                      onChange={onNativeFieldChange('staticProject')}
                      allowClear
                      showSearch
                    />
                  </Row>
                )}
                <Row style={{ marginTop: 10 }}>
                  Costcode
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    options={finalCostcodeFields}
                    optionFilterProp="label"
                    value={costcodeFieldId}
                    onChange={onNativeFieldChange('costcodeFieldId')}
                    allowClear
                  />
                </Row>
                {costcodeFieldId === 'staticCostcode' && (
                  <Row style={{ margin: '10px 0px' }}>
                    <CostcodesTreeSelector
                      projectId={staticProject}
                      divisionId={divisionId}
                      isClockIn
                      includeGlobal={false}
                      value={staticCostcode}
                      onChange={onNativeFieldChange('staticCostcode')}
                      style={{ width: '95%' }}
                    />
                  </Row>
                )}
              </>
            )}
            {enableTimecardSubmission && (
              <>
                <OnTraccrCheckbox
                  style={{ marginTop: 10 }}
                  value={autoSubmit}
                  onChange={onCheckChanged('autoSubmit')}
                  label="Auto Submit?"
                  hoverNode={(
                    <>
                      Check this box if you want the time entries to automatically be submitted
                      <br />
                      <br />
                      Leave this box unchecked if you want users to submit the time entries manually.
                    </>
                  )}
                />
                <OnTraccrCheckbox
                  style={{ marginTop: 5 }}
                  value={autoSubmit && autoApprove}
                  disabled={!autoSubmit}
                  onChange={onCheckChanged('autoApprove')}
                  label="Auto Approve?"
                  hoverNode={(
                    <>
                      Check this box if you want the time entries to automatically be approved
                      <br />
                      <br />
                      Leave this box unchecked if you want these time entries to be approved manually in the Time Cards page.
                    </>
                  )}
                />
              </>
            )}
            <OnTraccrCheckbox
              style={{ marginTop: 5 }}
              value={preventDuplicates}
              onChange={onCheckChanged('preventDuplicates')}
              // Title overflows if font is bigger
              label="Prevent Duplicates?"
              hoverNode={(
                <>
                  Check this box if you want Ontraccr to ignore new time entries that conflict
                  {' '}
                  with existing time entries
                  <br />
                  <br />
                  Leave this box unchecked if you want
                  {' '}
                  Ontraccr to create all entries, even if they conflict with existing entries.
                </>
              )}
            />
            <OnTraccrCheckbox
              style={{ marginTop: 5 }}
              value={allowFutureDates}
              onChange={onCheckChanged('allowFutureDates')}
              // Title overflows if font is bigger
              label="Allow Future Dates?"
              hoverNode={(
                <>
                  Check this box if you want Ontraccr to allow future dates
                  {' '}
                  for time entries
                  <br />
                  <br />
                  Leave this box unchecked if you want
                  {' '}
                  Ontraccr to restrict future dates for time entries.
                </>
              )}
            />
            {hasConfigurableCustomFields && (stateUseTable || stateUseTimeTrackingGrid) && (
              <Row style={{ margin: '20px 0px' }}>
                <BorderlessButton
                  title="Configure"
                  style={{ margin: 5 }}
                  iconNode={<SettingOutlined />}
                  onClick={openDrawer}
                />
                <WorkflowTimeConfigureDrawer
                  visible={showDrawer}
                  onClose={closeDrawer}
                  onSubmit={closeDrawer}
                  divisionId={divisionId}
                  divisionIds={relevantDivisionIds}
                  customFields={customFields}
                  sourceName={name}
                  sourceSections={sections}
                  fieldMappings={fieldMappings}
                  onFieldMappingsChange={onFieldMappingsChange}
                />
              </Row>
            )}
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
  WorkflowCreateTimeNodeComponent.propTypes = {
    id: PropTypes.string.isRequired,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    data: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  };

  WorkflowCreateTimeNodeComponent.defaultProps = {
    draggable: false,
    disabled: false,
    data: {},
  };

  return WorkflowCreateTimeNodeComponent;
}
